'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils, type InterfaceToken } from '@morpho-org/dapp-core';
import { NetworkAvatar as Network } from '@morpho-org/ui-v6/client';
import { Card } from '@morpho-org/ui-v6/server';
import VaultV2PositionRow from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2Summary/VaultV2PositionRow';
import VaultV2SummaryApyRow from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2Summary/VaultV2SummaryApyRow';
import VaultV2SummaryEarningsRow from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2Summary/VaultV2SummaryEarningsRow';
import type { VaultV1ActionPanelData } from '../../vaultV1ActionPanelData';
import { useVaultV1SummaryData } from './useVaultV1SummaryData';
interface VaultV1SummaryProps {
  vaultData: VaultV1ActionPanelData | null | undefined;
  vaultAsset: InterfaceToken | null | undefined;
  vaultAddress: Address;
  initialPosition: bigint | undefined;
  finalPosition: bigint | undefined;
  isLoading: boolean;
}
const VaultV1Summary = t0 => {
  const $ = _c(54);
  const {
    vaultData,
    vaultAsset,
    vaultAddress,
    initialPosition,
    finalPosition,
    isLoading
  } = t0;
  let t1;
  if ($[0] !== finalPosition || $[1] !== initialPosition || $[2] !== vaultAsset || $[3] !== vaultData) {
    t1 = {
      vaultData,
      vaultAsset,
      initialPosition,
      finalPosition
    };
    $[0] = finalPosition;
    $[1] = initialPosition;
    $[2] = vaultAsset;
    $[3] = vaultData;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const summaryData = useVaultV1SummaryData(t1);
  const chainId = vaultAsset?.chain.id;
  let t2;
  if ($[5] !== chainId) {
    t2 = chainId ? InterfaceChainUtils.getMetadata(chainId).chain.name : undefined;
    $[5] = chainId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const networkName = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-3xs text-secondary-foreground">Network</span>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      size: "small"
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== chainId) {
    t5 = <Network chainId={chainId} srcAvatarProps={t4} />;
    $[9] = chainId;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== networkName) {
    t6 = <span className="text-3xs">{networkName}</span>;
    $[11] = networkName;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <div className="flex min-h-[20px] flex-row items-center justify-between">{t3}<div className="gap-2xs flex flex-row items-center">{t5}{t6}</div></div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isLoading || $[17] !== summaryData.position || $[18] !== vaultAsset) {
    t8 = <VaultV2PositionRow vaultAsset={vaultAsset} position={summaryData.position} isLoading={isLoading} />;
    $[16] = isLoading;
    $[17] = summaryData.position;
    $[18] = vaultAsset;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== summaryData.apy.formatted || $[21] !== summaryData.apy.net || $[22] !== summaryData.apy.points || $[23] !== summaryData.apy.raw || $[24] !== summaryData.apy.rewards) {
    t9 = {
      raw: summaryData.apy.raw,
      formatted: summaryData.apy.formatted,
      net: summaryData.apy.net,
      points: summaryData.apy.points,
      rewards: summaryData.apy.rewards
    };
    $[20] = summaryData.apy.formatted;
    $[21] = summaryData.apy.net;
    $[22] = summaryData.apy.points;
    $[23] = summaryData.apy.raw;
    $[24] = summaryData.apy.rewards;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== chainId || $[27] !== vaultAddress) {
    t10 = chainId ? {
      kind: "vault",
      chainId,
      identifier: vaultAddress
    } : null;
    $[26] = chainId;
    $[27] = vaultAddress;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== summaryData.apy.performanceFee || $[30] !== t10 || $[31] !== t9 || $[32] !== vaultAsset) {
    t11 = <VaultV2SummaryApyRow performanceFee={summaryData.apy.performanceFee} apy={t9} vaultAsset={vaultAsset} merklContext={t10} />;
    $[29] = summaryData.apy.performanceFee;
    $[30] = t10;
    $[31] = t9;
    $[32] = vaultAsset;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== isLoading || $[35] !== summaryData.projectedEarnings.monthly.final || $[36] !== summaryData.projectedEarnings.monthly.finalTooltip || $[37] !== summaryData.projectedEarnings.monthly.initial || $[38] !== summaryData.projectedEarnings.monthly.initialTooltip || $[39] !== summaryData.projectedEarnings.monthly.showFinalValue) {
    t12 = <VaultV2SummaryEarningsRow label="Projected monthly earnings" initial={summaryData.projectedEarnings.monthly.initial} initialTooltip={summaryData.projectedEarnings.monthly.initialTooltip} final={summaryData.projectedEarnings.monthly.final} finalTooltip={summaryData.projectedEarnings.monthly.finalTooltip} showFinalValue={summaryData.projectedEarnings.monthly.showFinalValue} isLoading={isLoading} />;
    $[34] = isLoading;
    $[35] = summaryData.projectedEarnings.monthly.final;
    $[36] = summaryData.projectedEarnings.monthly.finalTooltip;
    $[37] = summaryData.projectedEarnings.monthly.initial;
    $[38] = summaryData.projectedEarnings.monthly.initialTooltip;
    $[39] = summaryData.projectedEarnings.monthly.showFinalValue;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== isLoading || $[42] !== summaryData.projectedEarnings.yearly.final || $[43] !== summaryData.projectedEarnings.yearly.finalTooltip || $[44] !== summaryData.projectedEarnings.yearly.initial || $[45] !== summaryData.projectedEarnings.yearly.initialTooltip || $[46] !== summaryData.projectedEarnings.yearly.showFinalValue) {
    t13 = <VaultV2SummaryEarningsRow label="Projected yearly earnings" initial={summaryData.projectedEarnings.yearly.initial} initialTooltip={summaryData.projectedEarnings.yearly.initialTooltip} final={summaryData.projectedEarnings.yearly.final} finalTooltip={summaryData.projectedEarnings.yearly.finalTooltip} showFinalValue={summaryData.projectedEarnings.yearly.showFinalValue} isLoading={isLoading} />;
    $[41] = isLoading;
    $[42] = summaryData.projectedEarnings.yearly.final;
    $[43] = summaryData.projectedEarnings.yearly.finalTooltip;
    $[44] = summaryData.projectedEarnings.yearly.initial;
    $[45] = summaryData.projectedEarnings.yearly.initialTooltip;
    $[46] = summaryData.projectedEarnings.yearly.showFinalValue;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== t11 || $[49] !== t12 || $[50] !== t13 || $[51] !== t7 || $[52] !== t8) {
    t14 = <Card className="gap-sm flex-col" withBorder={true}>{t7}{t8}{t11}{t12}{t13}</Card>;
    $[48] = t11;
    $[49] = t12;
    $[50] = t13;
    $[51] = t7;
    $[52] = t8;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  return t14;
};
export default VaultV1Summary;